import { Scene, Vector3, Raycaster, Clock, Fog } from 'three';

import CameraOrbitPositionable from './CameraOrbitPositionable.js';
import UIController from './UIController';
import ContentController from './ContentController';
import RenderController from './RenderController';
import LightController from './LightController';
import WebXRHandler from './WebXRHandler';

export default class Viewer {
	constructor(canvas, models, path, isMobile) {
		// Create Scene
		this.width = 0;
		this.height = 0;
		this.PATH = path;
		this.SCENE = new Scene();
		this.SCENE.fog = new Fog(0xffffff, 5, 6);
		this.CANVAS = canvas;

		// Create Elements - Order Important
		this.CAMERA_ORBIT = new CameraOrbitPositionable({
			radiusMin: 1,
			radiusMax: 4.5,
			radiusInitial: 7,
			radiusDelta: 0.1,
			cameraNear: 0.1,
			cameraFar: 7,
			rotationPhiMax: 0.1,
			rotationPhiMin: 2.5,
			rotationPhiInitial: 1.3,
			rotationThetaInitial: 0.6,
			positionLookat: new Vector3(0, 0.5, 0)
		});
		this.SCENE.add(this.CAMERA_ORBIT.getCamera());
		this.LIGHT_CONTROLLER = new LightController(
			this.SCENE,
			this.CAMERA_ORBIT.getCamera(),
			isMobile
		);
		this.RENDER_CONTROLLER = new RenderController(this.CANVAS, isMobile);
		this.CONTENT_CONTROLLER = new ContentController(
			this.SCENE,
			this.RENDER_CONTROLLER.RENDERER,
			this.PATH
		);

		this.CONTENT_CONTROLLER.addGLTF(`${models[0]}.gltf`);
		this.UI_CONTROLLER = new UIController(
			this.SCENE,
			this.CANVAS,
			this.CAMERA_ORBIT,
			this,
			this.CONTENT_CONTROLLER
		);
		// Start Render loop
		this.active = true;
		this.clock = new Clock();
		this.tick();

		const GLTFS = [`${models[0]}.gltf`, `${models[1]}.gltf`];
		const USDZS = [`${models[0]}.usdz`, `${models[1]}.usdz`];

		const self = this;
		const WEBXRHADLER = new WebXRHandler();

		// Doesn't work from local server, got to be hosted on a https server with a signed certificate
		// NOTE: Quite a few usdz models that work in regular views don't work in ARr
		function enterAR() {
			const MODEL = {
				gltf:
					window.location.href + self.PATH + GLTFS[self.UI_CONTROLLER.config],
				usdz:
					window.location.href + self.PATH + USDZS[self.UI_CONTROLLER.config],
				arTitle: 'Pikcells',
				arTitleLink: 'https://pikcells.com/',
				arFallbackLink: 'https://lab.pikcells.com/pikcells/ar-oven/0.1.0' // Shouldn't be needed
			};
			WEBXRHADLER.enterAR(MODEL);
		}

		document.querySelector('.ar').addEventListener('click', enterAR);
	}

	animate(intersect, animation) {
		this.CONTENT_CONTROLLER.animate(intersect, animation);
	}

	animateIn() {
		this.CAMERA_ORBIT.radiusTarget = 3;
		this.CAMERA_ORBIT.rotation_target_theta = -0.5;
	}

	setLightingFromJson(json) {
		this.LIGHT_CONTROLLER.setFromJSON(json.lights);
		this.RENDER_CONTROLLER.setFromJSON(json.renderer);
	}

	tick() {
		const delta = this.clock.getDelta();
		// Break loop when leaving View
		if (this.active === false) {
			return;
		}

		// Loop
		requestAnimationFrame(this.tick.bind(this));

		// Resize
		const W = this.CANVAS.clientWidth;
		const H = this.CANVAS.clientHeight;

		if (W !== this.width || H !== this.height) {
			this.setSize(W, H);

			this.width = W;
			this.height = H;
		}

		// Update - Order important
		this.CAMERA_ORBIT.update();

		// Render
		this.RENDER_CONTROLLER.render(this.SCENE, this.CAMERA_ORBIT.getCamera());
		this.CONTENT_CONTROLLER.tick(delta);
		requestAnimationFrame(this.UI_CONTROLLER.tick);
	}

	setSize(width, height) {
		// Get Canvas & Holder

		this.CANVAS.width = width;
		this.CANVAS.height = height;

		this.CAMERA_ORBIT.setSize(width, height);
		this.RENDER_CONTROLLER.setSize(width, height);
	}
}
